import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig';
import Swal from 'sweetalert2';
import AirDatePicker from '../components/ui/AirDatePicker';

function SetAccountBalance() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const accountName = queryParams.get('name') || '';
    const balance = queryParams.get('balance') || '0.00';
    const type = queryParams.get('type') || '';

    const isCashOrBank = accountName.toLowerCase().includes('cash') || accountName.toLowerCase().includes('bank');

    const [vouchers, setVouchers] = useState([]);
    const [banks, setBanks] = useState([]);
    const [parties, setParties] = useState([]);
    const [selectedParty, setSelectedParty] = useState(null);

    const [formData, setFormData] = useState({
        paymentDate: new Date().toISOString().split('T')[0],
        paymentTime: `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`,
        paymentAmount: '',
        paymentType: 'Credit',
        voucherType: '',
        bank: '',
        checkNumber: '',
        checkDate: '',
        remark: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = sessionStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };

                const [voucherRes, bankRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/voucher-type?limit=1000`, { headers }),
                    axios.get(`${API_BASE_URL}/bank?limit=1000`, { headers })
                ]);

                if (voucherRes.data && voucherRes.data.voucherTypes) setVouchers(voucherRes.data.voucherTypes);
                if (bankRes.data && bankRes.data.banks) setBanks(bankRes.data.banks);

                if (isCashOrBank) {
                    const [custRes, sellRes, transRes] = await Promise.all([
                        axios.get(`${API_BASE_URL}/customer?limit=1000`, { headers }),
                        axios.get(`${API_BASE_URL}/seller?limit=1000`, { headers }),
                        axios.get(`${API_BASE_URL}/transporter?limit=1000`, { headers })
                    ]);

                    const customersList = (custRes.data?.customers || custRes.data || []).map(c => ({
                        id: c.id || c.customerId || c._id,
                        partyName: c.customer_trade_name || c.customerName,
                        label: `[Customer] ${c.customer_trade_name || c.customerName}`
                    }));

                    const sellersList = (sellRes.data?.sellers || sellRes.data || []).map(s => ({
                        id: s.id || s.sellerId || s._id,
                        partyName: s.seller_trade_name || s.sellerName,
                        label: `[Seller] ${s.seller_trade_name || s.sellerName}`
                    }));

                    const transportersList = (transRes.data?.transporters || transRes.data || []).map(t => ({
                        id: t.id || t.transporterId || t._id,
                        partyName: t.transporterName,
                        label: `[Transporter] ${t.transporterName}`
                    }));

                    setParties([...customersList, ...sellersList, ...transportersList]);
                }
            } catch (err) {
                console.error("Error loading form data:", err);
            }
        };
        fetchData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            if (name === 'paymentType') {
                newData.voucherType = ''; // Reset voucher when payment type changes
            }
            return newData;
        });
    };

    const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;

        e.preventDefault();
        try {
            const token = sessionStorage.getItem('token');
            
            const entries = [];
            if (isCashOrBank && selectedParty) {
                // Double-entry bookkeeping:
                // 1. The Cash/Bank Account gets the opposite paymentType (Debit instead of Credit, or vice-versa)
                entries.push({
                    account: accountName || id,
                    paymentDate: formData.paymentDate,
                    paymentAmount: Number(formData.paymentAmount),
                    paymentType: formData.paymentType === 'Credit' ? 'Debit' : 'Credit',
                    bank: formData.bank,
                    checkNumber: formData.checkNumber,
                    checkDate: formData.checkDate,
                    remark: formData.remark
                });

                // 2. The Selected Party gets the chosen paymentType
                entries.push({
                    account: selectedParty.partyName || selectedParty.id,
                    paymentDate: formData.paymentDate,
                    paymentAmount: Number(formData.paymentAmount),
                    paymentType: formData.paymentType,
                    bank: formData.bank,
                    checkNumber: formData.checkNumber,
                    checkDate: formData.checkDate,
                    remark: formData.remark
                });
            } else {
                // Standard single entry fallback for general accounts
                entries.push({
                    account: accountName || id,
                    paymentDate: formData.paymentDate,
                    paymentAmount: Number(formData.paymentAmount),
                    paymentType: formData.paymentType,
                    bank: formData.bank,
                    checkNumber: formData.checkNumber,
                    checkDate: formData.checkDate,
                    remark: formData.remark
                });
            }

            const payload = {
                voucherType: formData.voucherType,
                entries: entries
            };

            await axios.post(`${API_BASE_URL}/voucher-entry`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success("Balance updated successfully!");
            navigate(-1);
        } catch (err) {
            console.error("Error saving balance:", err);
            toast.error("Failed to update balance");
        }
    };

    const isSeller = type === 'Seller';
    const isCustomer = type === 'Customer';

    const bannerBg = isSeller ? '#fff1f2' : isCustomer ? '#f0fdf4' : '#f0f9ff';
    const bannerBorder = isSeller ? '#e11d48' : isCustomer ? '#16a34a' : '#0284c7';
    const bannerColor = isSeller ? '#e11d48' : isCustomer ? '#15803d' : '#0369a1';
    const bannerShadow = isSeller 
        ? 'rgba(225, 29, 72, 0.05)' 
        : isCustomer 
        ? 'rgba(22, 163, 74, 0.05)' 
        : 'rgba(2, 132, 199, 0.05)';

    return (
        <div className="voucher-entry-container" style={{ maxWidth: '800px' }}>
            <h1 className="voucher-entry-title" style={{ textAlign: 'left', borderBottom: '2px solid #000', paddingBottom: '10px', width: 'fit-content' }}>
                Payment - {accountName}
            </h1>

            <div style={{
                marginTop: '15px',
                padding: '8px 16px',
                backgroundColor: bannerBg,
                borderLeft: `4px solid ${bannerBorder}`,
                borderRadius: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: '700',
                color: bannerColor,
                boxShadow: `0 1px 2px ${bannerShadow}`
            }}>
                Outstanding Balance: ₹{Math.abs(parseFloat(balance)).toFixed(2)}
            </div>

            <form onSubmit={handleSubmit} className="voucher-entry-form" style={{ marginTop: '30px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', marginBottom: '25px' }}>
                    {isCashOrBank && (
                        <div className="ve-field" style={{ gridColumn: 'span 2' }}>
                            <label>Party (Customer / Seller / Transporter)</label>
                            <select
                                value={selectedParty ? selectedParty.id : ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const match = parties.find(p => String(p.id) === String(val));
                                    setSelectedParty(match || null);
                                }}
                                required
                            >
                                <option value="">Select Party</option>
                                {parties.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="ve-field">
                        <label>Payment Date</label>
                        <AirDatePicker
                            value={formData.paymentDate}
                            onChange={(dateStr) => setFormData(prev => ({ ...prev, paymentDate: dateStr }))}
                            required
                        />
                    </div>
                    <div className="ve-field">
                        <label>Payment Time</label>
                        <input type="time" name="paymentTime" value={formData.paymentTime} onChange={handleChange} required />
                    </div>

                    <div className="ve-field">
                        <label>Payment Amount</label>
                        <input type="number" name="paymentAmount" placeholder="Payment Amount" value={formData.paymentAmount} onChange={handleChange} required />
                    </div>
                    <div className="ve-field">
                        <label>Payment Type</label>
                        <select name="paymentType" value={formData.paymentType} onChange={handleChange} required>
                            <option value="Credit">Credit</option>
                            <option value="Debit">Debit</option>
                        </select>
                    </div>

                    <div className="ve-field">
                        <label>Voucher Type</label>
                        <select name="voucherType" value={formData.voucherType} onChange={handleChange} required>
                            <option value="">Select Voucher</option>
                            {vouchers
                                .filter(v => {
                                    const name = (v.voucherTypeName || '').toLowerCase();
                                    if (formData.paymentType === 'Credit') {
                                        return name === 'bank receipt' || name === 'cash receipt';
                                    } else {
                                        return name === 'bank payment' || name === 'cash payment';
                                    }
                                })
                                .map(v => (
                                    <option key={v._id} value={v._id}>{v.voucherTypeName}</option>
                                ))
                            }
                        </select>
                    </div>
                    <div className="ve-field">
                        <label>Bank</label>
                        <select name="bank" value={formData.bank} onChange={handleChange}>
                            <option value="">Select Bank</option>
                            {banks.map(b => (
                                <option key={b._id} value={b.bankName}>{b.bankName}</option>
                            ))}
                        </select>
                    </div>

                    <div className="ve-field">
                        <label>Check Number</label>
                        <input type="text" name="checkNumber" placeholder="Payment Check Number" value={formData.checkNumber} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>Check Date</label>
                        <AirDatePicker
                            value={formData.checkDate}
                            onChange={(dateStr) => setFormData(prev => ({ ...prev, checkDate: dateStr }))}
                        />
                    </div>
                </div>

                <div className="ve-field" style={{ marginBottom: '30px' }}>
                    <label>Remark</label>
                    <textarea 
                        name="remark" 
                        placeholder="Enter Remark" 
                        value={formData.remark} 
                        onChange={handleChange}
                        style={{ height: '100px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '12px', outline: 'none' }}
                    />
                </div>

                <div style={{ display: 'flex', gap: '15px' }}>
                    <button type="submit" className="ve-submit-btn" style={{ width: '150px' }}>Submit</button>
                    <button type="button" onClick={() => navigate(-1)} className="ve-remove-btn" style={{ width: '150px', background: '#e5e7eb', color: '#6b7280', marginTop: '0' }}>Cancel</button>
                </div>
            </form>
        </div>
    );
}

export default SetAccountBalance;
